/**
 * WebSocket client for the GitLab Duo Agent Platform (DWS).
 *
 * Handles:
 * - WebSocket connection to `wss://{instance}/api/v4/ai/duo_workflows/ws`
 * - Sending ClientEvent messages (startRequest, actionResponse, stopWorkflow)
 * - Receiving WorkflowAction messages (newCheckpoint, runMcpTool, built-in tools)
 * - Dual heartbeat: ws.ping(45s) + JSON heartbeat(60s) — matching gitlab-lsp
 * - No reconnection on drop (matches gitlab-lsp behavior)
 */

import WebSocket from 'isomorphic-ws';
import { VERSION } from './version';
import type {
  ClientEvent,
  WorkflowAction,
  StartRequest,
  ActionResponsePayload,
  WorkflowClientEvent,
} from './gitlab-workflow-types';
import {
  WS_KEEPALIVE_PING_INTERVAL_MS,
  WS_HEARTBEAT_INTERVAL_MS,
  STOP_REASON_USER,
} from './gitlab-workflow-types';

export interface WorkflowWebSocketOptions {
  /** GitLab instance URL */
  instanceUrl: string;
  /** Model reference for DWS (e.g. 'anthropic/claude-sonnet-4-5-20250929' or 'default') */
  modelRef: string;
  /** Auth headers — must include Authorization */
  headers: Record<string, string>;
  /** Optional correlation ID */
  requestId?: string;
  /** Optional project context */
  projectId?: string;
  namespaceId?: string;
  rootNamespaceId?: string;
}

type EventCallback = (event: WorkflowClientEvent) => void;

export class GitLabWorkflowClient {
  private socket: WebSocket | null = null;
  private keepaliveInterval: ReturnType<typeof setInterval> | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private eventCallback: EventCallback | null = null;
  private closed = false;
  private lastSendTime = 0;

  /**
   * Connect to the DWS WebSocket and start listening for events.
   *
   * @param options - Connection parameters
   * @param onEvent - Callback invoked for each WorkflowClientEvent
   * @returns Promise that resolves when the connection is open
   */
  connect(options: WorkflowWebSocketOptions, onEvent: EventCallback): Promise<void> {
    this.validateOptions(options);
    this.eventCallback = onEvent;
    this.closed = false;
    this.cleanedUp = false;

    return new Promise<void>((resolve, reject) => {
      const wsUrl = this.buildWebSocketUrl(options);
      const wsHeaders = this.buildWebSocketHeaders(options);

      this.socket = new WebSocket(wsUrl, { headers: wsHeaders });
      let resolved = false;

      this.socket.onopen = () => {
        resolved = true;
        this.startKeepalive();
        this.startHeartbeat();
        resolve();
      };

      this.socket.onmessage = (event: WebSocket.MessageEvent) => {
        try {
          const data = typeof event.data === 'string' ? event.data : event.data.toString();
          const action = JSON.parse(data) as WorkflowAction;

          // Basic validation of message structure
          if (!action || typeof action !== 'object') {
            throw new Error('Invalid message structure: expected object');
          }

          this.handleAction(action);
        } catch (error) {
          this.emit({
            type: 'failed',
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
      };

      this.socket.onerror = (event: WebSocket.ErrorEvent) => {
        const error = new Error(`WebSocket error: ${event.message || 'unknown'}`);
        if (!resolved) {
          reject(error);
        } else {
          this.emit({ type: 'failed', error });
        }
      };

      this.socket.onclose = (event: WebSocket.CloseEvent) => {
        this.cleanup();
        if (!resolved) {
          reject(
            new Error(
              `WebSocket closed before open: code=${event.code} reason=${event.reason || ''}`
            )
          );
          return;
        }
        if (!this.closed) {
          this.emit({
            type: 'closed',
            code: event.code,
            reason: event.reason || '',
          });
        }
      };
    });
  }

  /**
   * Send a startRequest to begin the workflow.
   */
  sendStartRequest(request: StartRequest): void {
    this.send({ startRequest: request });
  }

  /**
   * Send an actionResponse (tool result) back to DWS.
   */
  sendActionResponse(requestID: string, response: string, error?: string | null): void {
    this.sendHeartbeatIfNeeded();
    const payload: ActionResponsePayload = {
      requestID,
      plainTextResponse: {
        response,
        error: error ?? null,
      },
    };
    this.send({ actionResponse: payload });
  }

  /**
   * Stop the workflow gracefully.
   */
  stop(): void {
    this.send({ stopWorkflow: { reason: STOP_REASON_USER } });
    this.closed = true;
  }

  /**
   * Close the WebSocket connection.
   */
  close(): void {
    // Set closed flag first to prevent race conditions
    if (this.closed) return;
    this.closed = true;

    this.cleanup();
    const sock = this.socket;
    this.socket = null;
    if (sock) {
      if (sock.readyState === WebSocket.OPEN || sock.readyState === WebSocket.CONNECTING) {
        sock.close(1000, 'Client closing');
      }
    }
  }

  /**
   * Check if the WebSocket is currently connected.
   */
  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private validateOptions(options: WorkflowWebSocketOptions): void {
    if (!options.instanceUrl || typeof options.instanceUrl !== 'string') {
      throw new Error('instanceUrl is required');
    }
    const parsed = new URL(options.instanceUrl);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      throw new Error(`Invalid instanceUrl protocol: ${parsed.protocol}`);
    }
    if (parsed.username || parsed.password) {
      throw new Error(
        'instanceUrl must not contain authentication credentials (username/password)'
      );
    }
    if (!options.headers || typeof options.headers !== 'object') {
      throw new Error('headers are required');
    }
    if (options.modelRef && typeof options.modelRef !== 'string') {
      throw new Error('modelRef must be a string');
    }
  }

  private buildWebSocketUrl(options: WorkflowWebSocketOptions): string {
    // Match gitlab-lsp URL construction exactly:
    // - NO workflow_id in URL (it goes in startRequest.workflowID instead)
    // - Only user_selected_model_identifier as query param
    const baseUrl = new URL(options.instanceUrl.replace(/\/?$/, '/'));
    const url = new URL('./api/v4/ai/duo_workflows/ws', baseUrl);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    if (options.modelRef && options.modelRef !== 'default') {
      // URL.searchParams.set() automatically encodes the value
      url.searchParams.set('user_selected_model_identifier', options.modelRef);
    }
    return url.toString();
  }

  private buildWebSocketHeaders(options: WorkflowWebSocketOptions): Record<string, string> {
    // Normalize incoming headers to lowercase keys to avoid duplicates
    const headers: Record<string, string> = {};
    for (const [key, value] of Object.entries(options.headers)) {
      headers[key.toLowerCase()] = value;
    }

    // Remove Content-Type — WebSocket upgrade (RFC 6455 §4.1) uses no body;
    // sending Content-Type can confuse proxies and fails some server checks.
    delete headers['content-type'];

    headers['x-gitlab-client-type'] = 'node-websocket';

    // Origin header — required by gitlab-lsp, validates WebSocket security
    // The origin is derived from the validated instanceUrl (already checked in validateOptions)
    const parsedUrl = new URL(options.instanceUrl);
    const origin = parsedUrl.origin;
    headers['origin'] = origin;

    if (options.requestId) {
      headers['x-request-id'] = options.requestId;
    }
    if (options.projectId) {
      headers['x-gitlab-project-id'] = options.projectId;
    }
    if (options.namespaceId) {
      headers['x-gitlab-namespace-id'] = options.namespaceId;
    }
    if (options.rootNamespaceId) {
      headers['x-gitlab-root-namespace-id'] = options.rootNamespaceId;
    }

    // User-Agent — set only if not already provided
    if (!headers['user-agent']) {
      headers['user-agent'] = `gitlab-ai-provider/${VERSION}`;
    }

    return headers;
  }

  private handleAction(action: WorkflowAction): void {
    // Checkpoint (text/status from LLM)
    if (action.newCheckpoint) {
      const checkpoint = action.newCheckpoint;

      this.emit({ type: 'checkpoint', data: checkpoint });

      if (checkpoint.status === 'FINISHED' || checkpoint.status === 'COMPLETED') {
        this.emit({ type: 'completed' });
      } else if (checkpoint.status === 'FAILED') {
        this.emit({
          type: 'failed',
          error: new Error(checkpoint.content || 'Workflow failed'),
        });
      } else if (checkpoint.status === 'STOPPED' || checkpoint.status === 'CANCELLED') {
        this.emit({ type: 'completed' });
      }
      return;
    }

    // MCP tool invocation
    if (action.runMCPTool && action.requestID) {
      this.emit({
        type: 'tool-request',
        requestID: action.requestID,
        data: action.runMCPTool,
      });
      return;
    }

    // Built-in tool invocations — field names match DWS protobuf wire format
    const builtinTools: Array<[string, unknown]> = [
      ['runReadFile', action.runReadFile],
      ['runReadFiles', action.runReadFiles],
      ['runWriteFile', action.runWriteFile],
      ['runShellCommand', action.runShellCommand],
      ['runEditFile', action.runEditFile],
      ['listDirectory', action.listDirectory],
      ['findFiles', action.findFiles],
      ['grep', action.grep],
      ['mkdir', action.mkdir],
      ['runCommand', action.runCommand],
      ['runGitCommand', action.runGitCommand],
      ['runHTTPRequest', action.runHTTPRequest],
    ];

    for (const [toolName, data] of builtinTools) {
      if (data && action.requestID) {
        this.emit({
          type: 'builtin-tool-request',
          requestID: action.requestID,
          toolName,
          data: data as Record<string, unknown>,
        });
        return;
      }
    }
  }

  private send(event: ClientEvent): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      const json = JSON.stringify(event);
      this.socket.send(json);
      this.lastSendTime = Date.now();
    }
  }

  private sendHeartbeatIfNeeded(): void {
    const elapsed = Date.now() - this.lastSendTime;
    if (elapsed >= WS_HEARTBEAT_INTERVAL_MS / 2) {
      this.send({ heartbeat: { timestamp: Date.now() } });
    }
  }

  private emit(event: WorkflowClientEvent): void {
    this.eventCallback?.(event);
  }

  /**
   * Start ws.ping() keepalive (45s interval).
   * Keeps TCP connection alive through proxies/load balancers.
   */
  private startKeepalive(): void {
    this.keepaliveInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        // ws-specific ping (not available in browser WebSocket, but
        // isomorphic-ws in Node.js delegates to the ws library)
        try {
          (this.socket as unknown as { ping: () => void }).ping();
        } catch {
          // Browser WebSocket doesn't have ping — silently skip
        }
      }
    }, WS_KEEPALIVE_PING_INTERVAL_MS);
  }

  /**
   * Start application-level heartbeat (60s interval).
   * Prevents DWS from timing out the workflow.
   */
  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.send({ heartbeat: { timestamp: Date.now() } });
    }, WS_HEARTBEAT_INTERVAL_MS);
  }

  private cleanedUp = false;

  /**
   * Clean up intervals. Idempotent — safe to call multiple times.
   */
  private cleanup(): void {
    if (this.cleanedUp) return;
    this.cleanedUp = true;

    if (this.keepaliveInterval) {
      clearInterval(this.keepaliveInterval);
      this.keepaliveInterval = null;
    }
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
}
